/**
 * DyoFlashSale Web Component
 * ============================================================
 * Propósito: Exibe um popup de flash sale dinâmico com ofertas personalizadas, cupons e contagem regressiva,
 * baseado em sessão do usuário, carrinho e condições de experimentação.
 *
 * Padrão de Arquitetura:
 * - Usa StatsigExperimentManager para gates e layers (gating de feature e parametrização de experimento).
 * - Usa ModelManager para buscar ofertas dinâmicas via API /pull/.
 * - Aguarda evento statsigReady antes de inicializar (garantindo contexto de experimentação pronto).
 * - Usa padrão getStatsigClient() (não acessar window.statsigClient diretamente).
 *
 * Visão Geral da Lógica:
 * 1. Aguarda evento statsigReady antes de chamar init().
 * 2. Em init, verifica se deve exibir o popup (localStorage/sessão).
 * 3. Usa StatsigExperimentManager para checar gate e obter parâmetros do layer.
 * 4. Usa ModelManager para buscar dados dinâmicos de oferta via API /pull/.
 * 5. Controla exibição de oferta, cópia de cupom, contagem regressiva e tracking de impressão.
 *
 * Consulte também: statsig-manager.js, statsig-dyo-model-test.js para comentários alinhados ao squad e arquitetura.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getStatsigClient, getOrCreateStatsigManager, // novo
ModelManager, buildModelRequestPayload, getSafeStorage, getSyncedUserId, Logger, syncDebugFlag, } from './statsig-manager.js';
const EXPERIMENT = {
    name: "dyo-flash-sale"
};
const DEBUG = false; // Altere para false para desativar logs deste teste
syncDebugFlag(EXPERIMENT.name, DEBUG);
/**
 * Componente Web DyoFlashSale
 * Classe principal do popup de flash sale dinâmico, com tipagem explícita.
 */
if (!customElements.get("dyo-flash-sale"))
    customElements.define("dyo-flash-sale", class DyoFlashSale extends HTMLElement {
        constructor() {
            super();
            // Link do modelo para API /pull/
            this.modelLink = null;
            // Hash do cupom para buscar cupom (se necessário)
            this.couponHash = null;
            // Stable ID do Statsig (identidade do usuário para experimentação/modelo)
            this.statsigStableId = null;
            // Nome do experimento alocado (do layer do Statsig)
            this.allocatedExperiment = null;
            // Timeout para próxima elegibilidade de oferta
            this.nextOfferTimeout = 0;
            // Timeout para popup inicial
            this.initialLoadTimeout = 0;
            // Contador de sessões (páginas visitadas no contexto relevante)
            this.sessionCount = 0;
            // Tipo de design (controla variante de UI)
            this.designType = null;
            // Indica se dados do flash sale estão carregados e ativos
            this.hasData = false;
            // Nome do layer e gate do Statsig (do atributo)
            this.statsigLayer = null;
            this.statsigGate = null;
            // Valor total original do carrinho
            this.cartOriginalTotalValue = 0;
            // Timeout para requisições à API do modelo (ms)
            this.REQUEST_TIMEOUT_MS = 10000;
            // Configuração da mecânica da oferta atual
            this.mechanicsConfig = null;
            // Descrição da oferta (do layer)
            this.offerDescription = "";
            // Inicializa atributos do componente a partir dos atributos HTML
            this.customerId = this.getAttribute("data-customer-id") || undefined;
            this.cartItemsIds = this.getAttribute("data-cart-item-ids") || undefined;
            this.statsigLayer = this.getAttribute("data-statsig-layer");
            this.statsigGate = this.getAttribute("data-statsig-gate");
            const giftMinValue = this.getAttribute("data-gift-min-value");
            const freeShippingValue = this.getAttribute("data-free-shipping-value");
            this.cartOriginalTotalValue = parseFloat(this.getAttribute("data-cart-original-total-price") || "0");
            this.modelManager = new ModelManager();
            this.mechanicsConfig = null;
            // Configurações de mecânica de oferta
            this.mechanicsConfigs = {
                gift: {
                    showFromTo: false,
                    minValue: giftMinValue,
                    addCoupon: false,
                },
                shipping: {
                    showFromTo: false,
                    minValue: freeShippingValue,
                    addCoupon: false,
                },
                discount_one_product: {
                    showFromTo: true,
                    addCoupon: true,
                },
                discount_all_products: {
                    showFromTo: true,
                    addCoupon: true,
                },
                suggestion: {
                    showFromTo: false,
                    addCoupon: false,
                },
            };
            this.handleCouponClickBound = this.handleCouponClick.bind(this);
            // Aguarda evento statsigReady antes de inicializar.
            window.addEventListener("statsigReady", () => {
                this.init();
            }, { once: true });
        }
        /**
         * Initializes the Flash Sale popup logic.
         * - Checks if the popup should be displayed based on session/localStorage.
         * - Uses StatsigExperimentManager for gate/layer checks.
         * - Sets up UI and listeners, and loads flash sale data if available.
         * - Waits for statsigReady before running (see constructor).
         */
        /**
         * Inicializa a lógica do popup de Flash Sale.
         * - Verifica se deve exibir o popup (localStorage/sessão).
         * - Usa StatsigExperimentManager para gate/layer.
         * - Configura UI e listeners, carrega dados do flash sale se disponíveis.
         * - Aguarda statsigReady antes de rodar (ver construtor).
         */
        init() {
            return __awaiter(this, void 0, void 0, function* () {
                try {
                    // Step 1: Check if we should display the popup (based on localStorage: closed state, offer expiry, session, etc)
                    const displayStatus = this.shouldDisplayFlashSale();
                    if (!displayStatus.shouldDisplay) {
                        return;
                    }
                    const flashSaleData = displayStatus.flashSaleData;
                    // Step 2: Update and retrieve session count (pages visited in relevant context)
                    this.sessionCount = this.getAndUpdateSessionCount();
                    // Step 3: Retrieve Statsig data using StatsigExperimentManager
                    // - Uses getStatsigClient() pattern (do NOT use window.statsigClient directly)
                    // - Checks gate, gets layer params for experiment configuration
                    const statsigData = yield this.getStatsigData();
                    if (!statsigData || (statsigData.minSessionCount > this.sessionCount && !flashSaleData)) {
                        return;
                    }
                    // Step 4: Set up UI event listeners for popup controls
                    this.addListeners();
                    // Step 5: Format UI elements with experiment/layer text
                    this.formatElements(statsigData.initialPopupTitle, statsigData.initialPopupButtonText, statsigData.offerCouponText, statsigData.countdownPopupTitle, statsigData.countdownPopupCalltext, statsigData.mechanics);
                    // Step 6: Validate minValue for offer eligibility (e.g., only show if cart below X)
                    if (!this.validateMinValue()) {
                        return;
                    }
                    // Step 7: If flash sale data already exists (from localStorage), show countdown and offer immediately
                    if (flashSaleData) {
                        try {
                            this.updateFlashSale(flashSaleData);
                            this.startCountdown(flashSaleData.expire_at);
                            this.hasData = true;
                        }
                        catch (error) {
                            this.logError("[Statsig][DyoFlashSale] Failed to update Flash Sale with data from local storage", false, error);
                        }
                    }
                    else {
                        // If no flash sale data, but eligible for next offer, show initial popup after delay
                        setTimeout(() => {
                            const discountCode = this.getCookie("discount_code");
                            // If discount code exists and is not a flash promo code, don't show
                            if (discountCode && discountCode.substring(0, 4) !== "FPDO") {
                                return;
                            }
                            this.elements.flashSaleInitialContainer.style.visibility = "visible";
                            this.pushImpression("initial");
                        }, this.initialLoadTimeout);
                    }
                }
                catch (error) {
                    this.logError("[Statsig][DyoFlashSale] Unexpected error in init()", false, error);
                }
            });
        }
        /**
         * Determines whether the flash sale should be displayed.
         *  Checks:
         *     1. If the flash sale popup should be hidden based on close info.
         *     2. If the flash sale data is available and not expired.
         *     3. If the flash sale data is not avaiable and the next offer date is **not** set.
         *
         * @returns {Object} An object containing the display status and next offer date.
         */
        /**
         * Determina se o flash sale deve ser exibido.
         * Faz as seguintes verificações:
         *   1. Se o popup deve estar oculto baseado em info de fechamento.
         *   2. Se há dados do flash sale válidos e não expirados.
         *   3. Se não há dados e não há timeout de próxima oferta.
         * @returns {Object} Status de exibição e dados do flash sale.
         */
        shouldDisplayFlashSale() {
            const storage = getSafeStorage();
            // Retrieve close info and next offer time from safe storage
            const flashSaleCloseInfo = JSON.parse(storage.getItem("flashSaleCloseInfo") || "null");
            const nextOfferDateStr = storage.getItem("flashSaleNextOffer");
            const nextOfferDate = nextOfferDateStr ? Number(nextOfferDateStr) : null;
            // Check if the flash sale popup should be hidden based on close info
            if (flashSaleCloseInfo) {
                const { closedAt, closedPageCount } = flashSaleCloseInfo;
                const timeDiff = Date.now() - closedAt;
                const pagesSinceClose = this.sessionCount - closedPageCount;
                // Hide popup for 3 pages or 1 hour after the last close
                if (pagesSinceClose < 4 && timeDiff < 1000 * 60 * 60) {
                    return { shouldDisplay: false };
                }
            }
            // Retrieve and validate flashSaleData (from safe storage)
            let flashSaleData = null;
            try {
                const parsed = JSON.parse(storage.getItem("flashSaleData") || "null");
                if (parsed &&
                    typeof parsed === "object" &&
                    "expire_at" in parsed &&
                    "kit_title" in parsed &&
                    "product_link" in parsed) {
                    flashSaleData = parsed;
                }
            }
            catch (_a) { }
            if (flashSaleData && flashSaleData.expire_at < Date.now()) {
                flashSaleData = null;
                storage.removeItem("flashSaleData");
            }
            // Validação do nextOfferDate
            let validNextOfferDate = nextOfferDate;
            if (nextOfferDate && nextOfferDate < Date.now()) {
                storage.removeItem("flashSaleNextOffer");
                validNextOfferDate = null;
            }
            // Decide se deve exibir algo
            const shouldDisplay = !!flashSaleData || (!flashSaleData && !validNextOfferDate);
            return { shouldDisplay, nextOfferDate: validNextOfferDate, flashSaleData };
        }
        /**
         * Retrieves and updates the session count from localStorage.
         *
         * @returns {number} - The updated session count.
         */
        /**
         * Recupera e atualiza o contador de sessões do localStorage.
         * @returns {number} O contador atualizado de sessões.
         */
        getAndUpdateSessionCount() {
            const storage = getSafeStorage();
            let sessionCountObj = null;
            try {
                sessionCountObj = JSON.parse(storage.getItem("flashSaleSessionCount") || "null");
            }
            catch (_a) {
                sessionCountObj = null;
            }
            const paths = window.location.pathname.split("/");
            const isValidPage = paths.length > 1 && paths[1] === "products";
            if (!sessionCountObj || sessionCountObj.expireAt < Date.now()) {
                if (!isValidPage) {
                    return 0;
                }
                storage.setItem("flashSaleSessionCount", JSON.stringify({ count: 1, expireAt: Date.now() + 1000 * 60 * 60 * 1 }) // 1 hora
                );
                return 1;
            }
            if (isValidPage) {
                sessionCountObj.count += 1;
            }
            sessionCountObj.expireAt = Date.now() + 1000 * 60 * 60 * 1; // +1h
            storage.setItem("flashSaleSessionCount", JSON.stringify(sessionCountObj));
            return sessionCountObj.count;
        }
        /**
         * Fetches data from Statsig and determines if the flash sale should be displayed.
         *
         * Uses StatsigExperimentManager to:
         *   - Check if the experiment gate is open for this user/session.
         *   - Retrieve layer parameters for the flash sale experiment.
         *   - Extracts model link, experiment name, and UI text from layer.
         *   - Gets stableID for user identity (for model API).
         * @returns {Object|null} - The Statsig data or null if conditions are not met.
         */
        /**
         * Busca dados do Statsig e determina se deve exibir o flash sale.
         * Usa StatsigExperimentManager para:
         *   - Checar se o gate está aberto para usuário/sessão.
         *   - Obter parâmetros do layer para configuração do experimento.
         *   - Extrai model link, nome do experimento, textos de UI do layer.
         *   - Obtém stableID para identidade do usuário (API do modelo).
         * @returns {StatsigLayerData|null} Dados do Statsig ou null se não elegível.
         */
        getStatsigData() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                try {
                    // Use shared StatsigExperimentManager instance for all Statsig checks
                    const experimentManager = yield getOrCreateStatsigManager();
                    Logger.log(EXPERIMENT.name, "[Statsig][DyoFlashSale] Experiment created", experimentManager);
                    // 1. Check gate (feature flag) to see if user is eligible for flash sale
                    const gateOpen = yield experimentManager.checkGate(this.statsigGate);
                    if (!gateOpen) {
                        return null;
                    }
                    // 2. Get experiment layer params for this user
                    const layer = yield experimentManager.getLayerParams(this.statsigLayer);
                    if (!layer)
                        return null;
                    // 3. Get model API link from layer config
                    this.modelLink = layer.get("model-link");
                    if (!this.modelLink) {
                        return null;
                    }
                    // 4. Get allocated experiment name (for logging/model)
                    this.allocatedExperiment = layer.allocatedExperimentName;
                    // 5. Get stableID from StatsigClient (for model API)
                    const stableContext = yield ((_a = getStatsigClient()) === null || _a === void 0 ? void 0 : _a.getAsyncContext());
                    this.statsigStableId = stableContext === null || stableContext === void 0 ? void 0 : stableContext.stableID;
                    // 6. Get all required UI and config params from layer (with defaults)
                    const initialPopupTitle = layer.get("initial-popup-title", "FLASH PROMO SÓ PARA VOCÊ");
                    const initialPopupButtonText = layer.get("initial-popup-button-text", "CONFERIR");
                    const offerCouponText = layer.get("offer-popup-button", "DESBLOQUEAR CUPOM");
                    const countdownPopupTitle = layer.get("countdown-popup-title", "FLASH PROMO EXCLUSIVA");
                    const countdownPopupCalltext = layer.get("countdown-popup-calltext", "ACABA EM");
                    const mechanics = layer.get("mechanics", "discount_one_product");
                    this.offerDescription = layer.get("offer-popup-description", "ADICIONE AO CARRINHO E DESBLOQUEIE UM BRINDE");
                    this.nextOfferTimeout = layer.get("next-offer-timeout", 1000 * 60 * 60 * 24 * 1);
                    this.initialLoadTimeout = layer.get("initial-load-timeout", 7 * 1000);
                    this.designType = layer.get("design-type", 2);
                    // 7. Get minimum session count for eligibility (mobile vs desktop)
                    let minSessionCount;
                    if (window.innerWidth <= 768) {
                        minSessionCount = layer.get("mobile-session-count", 1);
                    }
                    else {
                        minSessionCount = layer.get("desktop-session-count", 1);
                    }
                    return {
                        initialPopupTitle,
                        initialPopupButtonText,
                        minSessionCount,
                        offerCouponText,
                        countdownPopupTitle,
                        countdownPopupCalltext,
                        mechanics,
                    };
                }
                catch (error) {
                    this.logError("[Statsig][DyoFlashSale] Failed to fetch Statsig Data", false, error);
                    return null;
                }
            });
        }
        /**
         * Adds necessary event listeners to the component elements.
         *
         * Sets up click handlers for popup open/close, offer reveal, and coupon.
         */
        /**
         * Adiciona listeners de eventos necessários aos elementos do componente.
         * Configura handlers para abrir/fechar popup, revelar oferta e copiar cupom.
         */
        addListeners() {
            this.elements = {
                flashSaleInitialContainer: document.querySelector(`.flash-sale-${this.designType}-initial-container`),
                flashSaleInitialCloseButton: document.querySelector(`#flash-sale-${this.designType}-close-button`),
                flashSaleInitialTitle: document.querySelector(`#flash-sale-${this.designType}-initial-title`),
                flashSaleInitialButton: document.querySelector(`#flash-sale-${this.designType}-initial-button`),
                flashSaleOfferCloseButton: document.querySelector(`#flash-sale-${this.designType}-offer-close-button`),
                flashSaleOfferContent: document.querySelector(`.flash-sale-${this.designType}-offer-content`),
                flashSaleOfferCoupon: document.querySelector(`#flash-sale-${this.designType}-offer-coupon`),
                flashSaleOfferImage: document.querySelector(`#flash-sale-${this.designType}-offer-image`),
                flashSaleOfferKit: document.querySelector(`#flash-sale-${this.designType}-offer-kit`),
                flashSaleOfferDescription: document.querySelector(`#flash-sale-${this.designType}-offer-description`),
                flashSaleOfferDiscount: document.querySelector(`#flash-sale-${this.designType}-offer-discount`),
                flashSaleOfferFromToContainer: document.querySelector(`.flash-sale-${this.designType}-de-por`),
                flashSaleOfferFromPrice: document.querySelector(`#flash-sale-${this.designType}-from-price`),
                flashSaleOfferForPrice: document.querySelector(`#flash-sale-${this.designType}-for-price`),
                flashSaleOfferDePor: document.querySelector(`.flash-sale-${this.designType}-de-por`),
                flashSaleCountdownContainer: document.querySelector(`.flash-sale-${this.designType}-countdown-container`),
                flashSaleCountdownTitle: document.querySelector(`#flash-sale-${this.designType}-countdown-title`),
                flashSaleCountdownCalltext: document.querySelector(`#flash-sale-${this.designType}-countdown-calltext`),
                flashSaleCountdownText: document.querySelector(`#flash-sale-${this.designType}-countdown-timer`),
                flashSaleCountdownClose: document.querySelector(`#flash-sale-${this.designType}-countdown-close`),
                flashSaleCountdownIndication: document.querySelector(`#flash-sale-${this.designType}-countdown-indication`),
            };
            // Initial popup: open offer on click, or close if close button clicked
            this.elements.flashSaleInitialContainer.addEventListener("click", (event) => {
                this.elements.flashSaleInitialContainer.style.visibility = "hidden";
                if (event.target !== this.elements.flashSaleInitialCloseButton) {
                    this.elements.flashSaleOfferContent.style.visibility = "visible";
                    this.getFlashSaleData();
                }
                else {
                    const flashSaleCloseInfo = {
                        closedAt: Date.now(),
                        closedPageCount: this.sessionCount,
                    };
                    localStorage.setItem("flashSaleCloseInfo", JSON.stringify(flashSaleCloseInfo));
                }
            });
            // Countdown popup: open offer on click, or close if close button clicked
            this.elements.flashSaleCountdownContainer.addEventListener("click", (event) => {
                if (event.target !== this.elements.flashSaleCountdownClose) {
                    this.elements.flashSaleOfferContent.style.visibility = "visible";
                }
                else {
                    this.elements.flashSaleCountdownContainer.style.visibility = "hidden";
                    const flashSaleCloseInfo = {
                        closedAt: Date.now(),
                        closedPageCount: this.sessionCount,
                    };
                    localStorage.setItem("flashSaleCloseInfo", JSON.stringify(flashSaleCloseInfo));
                }
            });
            // Offer popup: close on close button, or hide on outside click
            this.elements.flashSaleOfferCloseButton.addEventListener("click", () => {
                this.elements.flashSaleOfferContent.style.visibility = "hidden";
                if (this.hasData) {
                    this.elements.flashSaleCountdownContainer.style.visibility = "visible";
                }
            });
            this.elements.flashSaleOfferContent.addEventListener("click", (event) => {
                const target = event.target;
                if (!this.elements.flashSaleOfferContent.contains(target) &&
                    target !== this.elements.flashSaleInitialButton) {
                    this.elements.flashSaleOfferContent.style.visibility = "hidden";
                }
            });
        }
        /**
         * Formats the elements with the provided text content.
         *
         * @param {string} initialPopupTitle - The title of the initial popup.
         * @param {string} initialPopupButtonText - The text for the initial popup button.
         * @param {string} offerCouponText - The text for the offer coupon button.
         * @param {string} countdownPopupTitle - The title of the countdown popup.
         * @param {string} countdownPopupCalltext - The call to action text for the countdown popup.
         * @param {string} mechanics - The mechanics of the offer.
         */
        /**
         * Formata elementos do popup com textos fornecidos.
         * @param initialPopupTitle Título do popup inicial.
         * @param initialPopupButtonText Texto do botão do popup inicial.
         * @param offerCouponText Texto do botão do cupom.
         * @param countdownPopupTitle Título do popup de contagem.
         * @param countdownPopupCalltext Texto de call to action do countdown.
         * @param mechanics Mecânica da oferta.
         */
        formatElements(initialPopupTitle, initialPopupButtonText, offerCouponText, countdownPopupTitle, countdownPopupCalltext, mechanics) {
            this.elements.flashSaleInitialTitle.innerHTML = initialPopupTitle;
            this.elements.flashSaleInitialButton.textContent = initialPopupButtonText;
            this.elements.flashSaleOfferCoupon.textContent = offerCouponText;
            this.elements.flashSaleCountdownTitle.innerHTML = countdownPopupTitle;
            this.elements.flashSaleCountdownCalltext.innerHTML = countdownPopupCalltext;
            this.mechanicsConfig = this.mechanicsConfigs[mechanics];
            if (!this.mechanicsConfig.showFromTo) {
                this.elements.flashSaleOfferFromToContainer.style.visibility = "hidden";
            }
        }
        /**
         * Validates if the cart value is below the minValue for the current offer mechanic.
         * @returns {boolean} True if eligible, false otherwise.
         */
        /**
         * Valida se o valor do carrinho está abaixo do minValue para a mecânica atual.
         * @returns {boolean} True se elegível, false caso contrário.
         */
        validateMinValue() {
            if (this.mechanicsConfig &&
                this.mechanicsConfig.minValue !== undefined &&
                this.cartOriginalTotalValue > Number(this.mechanicsConfig.minValue)) {
                return false;
            }
            return true;
        }
        /**
         * Handles the coupon click event.
         * Calls getCoupon() to fetch and apply the coupon.
         */
        /**
         * Handler para clique no cupom.
         * Chama getCoupon() para buscar e aplicar o cupom.
         */
        handleCouponClick() {
            this.getCoupon();
        }
        /**
         * Fetches flash sale data from the dynamic model API.
         * - Uses ModelManager to call /pull/ endpoint.
         * - Stores result in localStorage and updates UI.
         * - Uses Statsig stableID, allocated experiment, and full context for API call.
         */
        /**
         * Busca dados do flash sale via API dinâmica do modelo.
         * Usa ModelManager para chamar /pull/.
         * Salva resultado no localStorage e atualiza UI.
         */
        getFlashSaleData() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a;
                // Função local para obter cart no formato { product_id, variant_id, quantity }
                function getCartDataFlashSale() {
                    var _a, _b;
                    try {
                        const cartItems = (_b = (_a = window === null || window === void 0 ? void 0 : window.Shopify) === null || _a === void 0 ? void 0 : _a.cartItems) !== null && _b !== void 0 ? _b : [];
                        if (!Array.isArray(cartItems) || cartItems.length === 0) {
                            Logger.warn(EXPERIMENT.name, '[Statsig][Manager][FlashSale] Cart is empty or unavailable');
                            return [];
                        }
                        return cartItems.map((item) => ({
                            product_id: item.product_id,
                            variant_id: item.variant_id,
                            quantity: item.quantity
                        }));
                    }
                    catch (error) {
                        Logger.error(EXPERIMENT.name, '[Statsig][Manager][FlashSale] Failed to parse Shopify.cartItems', error);
                        return [];
                    }
                }
                try {
                    const storage = getSafeStorage();
                    Logger.log(EXPERIMENT.name, "[Statsig][DyoFlashSale] Preparing model payload");
                    // Build payload using getSyncedUserId
                    const syncedIds = getSyncedUserId({ customer_id: this.customerId, stable_id: this.statsigStableId });
                    // Monta o payload base incluindo cart: getCartDataFlashSale()
                    const payload = Object.assign(Object.assign({}, yield buildModelRequestPayload(Object.assign(Object.assign({}, syncedIds), { allocated_experiment: (_a = this.allocatedExperiment) !== null && _a !== void 0 ? _a : undefined }), [
                        "customer_id",
                        "stable_id",
                        "allocated_experiment",
                        "cart",
                        "utms",
                        "products",
                        "collections"
                    ])), { cart: getCartDataFlashSale() });
                    const existingDataRaw = storage.getItem("flashSaleData");
                    let existingData = null;
                    try {
                        existingData = existingDataRaw ? JSON.parse(existingDataRaw) : null;
                    }
                    catch (_b) { }
                    if (existingData && typeof existingData === "object") {
                        Logger.log(EXPERIMENT.name, "[Statsig][DyoFlashSale] Found persisted flashSaleData. Updating with latest context:", payload);
                        if (existingData.modelUrl) {
                            yield this.modelManager.getModel(existingData.modelUrl, payload, { timeout: this.REQUEST_TIMEOUT_MS });
                        }
                        storage.setItem("statsig:model:request:flash-sale", JSON.stringify({
                            timestamp: new Date().toISOString(),
                            payload
                        }));
                        return;
                    }
                    Logger.log(EXPERIMENT.name, "No persisted flashSaleData found. Calling model...");
                    const modelData = yield this.modelManager.getModel(this.modelLink, payload, { timeout: this.REQUEST_TIMEOUT_MS });
                    if (modelData &&
                        typeof modelData === "object" &&
                        "expire_at" in modelData &&
                        "kit_title" in modelData &&
                        "product_link" in modelData) {
                        const flashSaleData = modelData;
                        storage.setItem("flashSaleData", JSON.stringify(flashSaleData));
                        storage.setItem("flashSaleNextOffer", String(Date.now() + this.nextOfferTimeout));
                        this.updateFlashSale(flashSaleData);
                        this.startCountdown(flashSaleData.expire_at);
                        this.hasData = true;
                    }
                }
                catch (error) {
                    this.logError("[Statsig][DyoFlashSale] Failed to fetch flash sale data", true, error);
                    return;
                }
            });
        }
        /**
         * Updates the popup with the given flash sale data.
         *
         * @param {Object} flashSaleData - The data for updating the popup.
         */
        /**
         * Atualiza o popup com os dados fornecidos do flash sale.
         * @param flashSaleData Dados para atualizar o popup.
         */
        updateFlashSale(flashSaleData) {
            const img = document.createElement("img");
            if (!!flashSaleData.mobile_image_url && window.innerWidth <= 768) {
                img.src = flashSaleData.mobile_image_url;
            }
            else {
                img.src = flashSaleData.image_url;
            }
            img.style.width = "100%";
            img.style.height = "auto";
            // Only update the innerHTML after the image has loaded
            img.onload = () => {
                this.elements.flashSaleOfferImage.replaceChildren(img);
            };
            // But load all content when possible
            // Set the text
            this.elements.flashSaleOfferKit.innerHTML = flashSaleData.kit_title;
            if (this.mechanicsConfig.showFromTo) {
                this.elements.flashSaleOfferFromPrice.innerHTML = flashSaleData.from_price;
                this.elements.flashSaleOfferForPrice.innerHTML = flashSaleData.for_price;
                this.elements.flashSaleOfferDiscount.innerHTML = flashSaleData.discount_tag;
                this.elements.flashSaleOfferDescription.innerHTML = "";
            }
            else {
                this.elements.flashSaleOfferDescription.innerHTML = this.offerDescription;
                this.elements.flashSaleOfferDiscount.innerHTML = "";
            }
            // Update skeletons
            this.elements.flashSaleOfferDePor.querySelectorAll(".skeleton").forEach((el) => el.classList.remove("skeleton"));
            this.elements.flashSaleOfferCoupon.classList.remove("skeleton");
            // Add event listener to redirect to product page
            this.elements.flashSaleOfferContent.addEventListener("click", (event) => {
                // Fix: EventTarget is not Node, so cast to Node
                const target = event.target;
                if (target !== this.elements.flashSaleOfferCoupon &&
                    target !== this.elements.flashSaleOfferCloseButton) {
                    window.location.href = flashSaleData.product_link;
                }
            });
            this.pushImpression("offer");
            if (flashSaleData.coupon) {
                this.updateCoupon(flashSaleData.coupon);
            }
            else {
                this.couponHash = flashSaleData.coupon_hash;
                this.elements.flashSaleOfferCoupon.addEventListener("click", this.handleCouponClickBound);
            }
        }
        /**
         * Starts the countdown timer for the flash sale offer.
         *
         * @param {number} expireAt - The timestamp when the offer expires.
         */
        /**
         * Inicia o timer de contagem regressiva para a oferta do flash sale.
         * @param expireAt Timestamp de expiração da oferta.
         */
        startCountdown(expireAt) {
            const updateCountdown = () => {
                const now = Date.now();
                const timeDifference = expireAt - now;
                if (timeDifference > 0) {
                    const hours = Math.floor((timeDifference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                    const minutes = Math.floor((timeDifference % (1000 * 60 * 60)) / (1000 * 60));
                    if (hours === 0) {
                        this.elements.flashSaleCountdownText.textContent = `${minutes.toString().padStart(2, "0")}`;
                        this.elements.flashSaleCountdownIndication.textContent = "MINUTOS";
                    }
                    else {
                        this.elements.flashSaleCountdownText.textContent = `${hours.toString().padStart(2, "0")}:${minutes
                            .toString()
                            .padStart(2, "0")}`;
                        this.elements.flashSaleCountdownIndication.textContent = "HORAS";
                    }
                }
                else {
                    this.elements.flashSaleCountdownText.textContent = "00:00";
                    setTimeout(() => (this.elements.flashSaleCountdownContainer.style.visibility = "hidden"), 300);
                }
            };
            setInterval(updateCountdown, 1000 * 60);
            updateCountdown();
            this.elements.flashSaleCountdownContainer.style.visibility = "visible";
            this.elements.flashSaleInitialContainer.style.visibility = "hidden";
        }
        /**
         * Updates the coupon element with the provided coupon code.
         *
         * @param {string} coupon - The coupon code.
         */
        /**
         * Atualiza elemento do cupom com o código fornecido.
         * @param coupon Código do cupom.
         */
        updateCoupon(coupon) {
            const storage = getSafeStorage();
            this.elements.flashSaleOfferCoupon.textContent = "Copiar: " + coupon;
            this.elements.flashSaleOfferCoupon.addEventListener("click", () => {
                navigator.clipboard.writeText(coupon);
                this.elements.flashSaleOfferCoupon.textContent = "Copiado!";
                setTimeout(() => {
                    this.elements.flashSaleOfferCoupon.textContent = "Copiar: " + coupon;
                }, 2000);
            });
        }
        /**
         * Fetches the coupon using the coupon hash and redirects the user to the product page.
         * If addCoupon is enabled, fetches coupon from backend and applies it.
         */
        /**
         * Busca o cupom usando o hash e redireciona para a página do produto.
         * Se addCoupon está habilitado, busca cupom do backend e aplica.
         */
        getCoupon() {
            return __awaiter(this, void 0, void 0, function* () {
                const storage = getSafeStorage();
                if (!this.mechanicsConfig.addCoupon) {
                    let flashSaleData = null;
                    try {
                        const parsed = JSON.parse(storage.getItem("flashSaleData") || "null");
                        if (parsed &&
                            typeof parsed === "object" &&
                            "expire_at" in parsed &&
                            "kit_title" in parsed &&
                            "product_link" in parsed) {
                            flashSaleData = parsed;
                        }
                    }
                    catch (_a) { }
                    if (!flashSaleData) {
                        this.elements.flashSaleOfferCoupon.textContent = "Tente novamente mais tarde";
                        return;
                    }
                    window.location.href = flashSaleData.product_link;
                    return;
                }
                const cfLink = "https://us-central1-insider-data-lake.cloudfunctions.net/dynamic-offers-coupon";
                const requestBody = { coupon_hash: this.couponHash, stable_id: this.statsigStableId };
                this.elements.flashSaleOfferCoupon.removeEventListener("click", this.handleCouponClickBound);
                try {
                    this.elements.flashSaleOfferCoupon.textContent = "Carregando...";
                    const couponRes = yield fetch(cfLink, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify(requestBody),
                    });
                    if (!couponRes.ok) {
                        throw new Error(`HTTP error! status: ${couponRes.status}`);
                    }
                    const data = yield couponRes.json();
                    const coupon = data.coupon;
                    // Fix: Only assign if valid object, not empty object
                    let flashSaleData = null;
                    try {
                        const parsed = JSON.parse(storage.getItem("flashSaleData") || "null");
                        if (parsed && typeof parsed === "object" && "product_link" in parsed) {
                            flashSaleData = parsed;
                        }
                    }
                    catch (_b) { }
                    if (!flashSaleData) {
                        this.elements.flashSaleOfferCoupon.textContent = "Tente novamente mais tarde";
                        return;
                    }
                    flashSaleData.coupon = coupon;
                    storage.setItem("flashSaleData", JSON.stringify(flashSaleData));
                    navigator.clipboard.writeText(coupon);
                    const productLink = new URL(flashSaleData.product_link);
                    this.elements.flashSaleOfferCoupon.textContent = "Cupom aplicado. Indo para página do produto...";
                    setTimeout(() => (window.location.href = `${window.location.origin}/discount/${coupon}?redirect=${productLink.pathname}`), 2000);
                }
                catch (error) {
                    this.logError(EXPERIMENT.name + " [DynamicOffers][FlashSale] Failed to generate coupon", false, error);
                    this.elements.flashSaleOfferCoupon.textContent = "Tente novamente mais tarde";
                }
            });
        }
        /**
         * Pushes an impression to the dataLayer and hence to GA4.
         *
         * @param {string} type - The type of impression (e.g., "initial", "offer").
         */
        /**
         * Envia impressão para o dataLayer (GA4).
         * @param type Tipo de impressão ("initial", "offer", etc).
         */
        pushImpression(type) {
            window.dataLayer.push({
                event: `FSDO_${type}_impression`,
                timestamp: new Date().toISOString(),
            });
        }
        /**
         * Retrieves a cookie value by name.
         *
         * @param {string} name - The name of the cookie.
         * @returns {string|null} - The cookie value or null if not found.
         */
        /**
         * Recupera valor de cookie pelo nome.
         * @param name Nome do cookie.
         * @returns Valor do cookie ou null.
         */
        getCookie(name) {
            var _a;
            const value = `; ${document.cookie}`;
            const parts = value.split(`; ${name}=`);
            if (parts.length === 2)
                return ((_a = parts.pop()) === null || _a === void 0 ? void 0 : _a.split(";").shift()) || null;
            return null;
        }
        /**
         * Logs an error to the console and sends it to Rollbar.
         *
         * @param {string} message - The error message.
         * @param {boolean} [showError=false] - Whether to display the error to the user.
         * @param {Error} [error] - The error object.
         */
        /**
         * Loga erro no console e envia para o Rollbar.
         * @param message Mensagem de erro.
         * @param showError Se deve exibir erro para usuário.
         * @param error Objeto de erro.
         */
        logError(message, showError = false, error) {
            if (showError) {
                this.elements.flashSaleOfferKit.innerHTML = "Erro carregando. Tente novamente mais tarde.";
            }
            // eslint-disable-next-line no-console
            if (error) {
                Logger.error(EXPERIMENT.name, message, error, {
                    category: "DynamicOffers",
                    component: "FlashSale",
                    cartItemsIds: this.cartItemsIds,
                });
            }
            else {
                Logger.error(EXPERIMENT.name, message, {
                    category: "DynamicOffers",
                    component: "FlashSale",
                    cartItemsIds: this.cartItemsIds,
                });
            }
        }
    });
